import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@mdvip/shared/components/ui/select';
import { Skeleton } from '@mdvip/shared/components/ui/skeleton';
import { useId } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { type DatasetState, PAGE_SIZES, type PageSize } from '@/hooks/member-views/useMemberDataset';
import type { MemberRowData, MemberTable } from '@/lib/member-views/memberDataset.types';

export interface ResultsSummaryProps<TRow extends MemberRowData> {
  table: MemberTable<TRow>;
  state: DatasetState;
}

/**
 * ResultsSummary — "Showing 10 of 248 patients" and the rows-per-page select, above the table.
 *
 * The count is announced from its own visually hidden status, which only ever holds the settled
 * sentence: the skeleton shown while loading never takes a turn in the screen reader's queue.
 * Hidden when the view failed to load — there's nothing to count.
 */
export function ResultsSummary<TRow extends MemberRowData>({ table, state }: ResultsSummaryProps<TRow>) {
  const { t } = useTranslation('providers');
  const pageSizeId = useId();

  if (state === 'error') return null;
  const loading = state === 'loading';

  return (
    <table.Subscribe selector={(tableState) => tableState.pagination}>
      {(pagination) => {
        const count = table.getRowModel().rows.length;
        // Every row matching the selected letter, across all pages.
        const total = table.getPrePaginatedRowModel().rows.length;
        const values = { showing: String(count !== total), count, total };

        return (
          <div className="flex flex-wrap items-center gap-4">
            <div className="min-w-0 flex-1">
              {loading ? (
                <Skeleton className="h-5 w-40" />
              ) : (
                <p className="text-sm text-foreground">
                  <Trans
                    t={t}
                    i18nKey="memberViews.results"
                    values={values}
                    // Trans clones this element into the sentence; a key keeps React's list check quiet.
                    components={{ bold: <span key="count" className="font-semibold" /> }}
                  />
                </p>
              )}
            </div>
            <span role="status" aria-live="polite" className="sr-only">
              {!loading && (
                <Trans
                  t={t}
                  i18nKey="memberViews.results"
                  values={values}
                  components={{ bold: <span key="count" /> }}
                />
              )}
            </span>
            <div className="flex items-center gap-2">
              <label htmlFor={pageSizeId} className="text-sm text-muted-foreground">
                {t('memberViews.pageSize.show')}
              </label>
              <Select<PageSize>
                value={pagination.pageSize as PageSize}
                // The table routes a size change through the hook, which goes back to page 1.
                onValueChange={(size) => {
                  if (size !== null) table.setPageSize(size);
                }}
              >
                <SelectTrigger id={pageSizeId} size="sm" className="w-24">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PAGE_SIZES.map((size) => (
                    <SelectItem key={size} value={size}>
                      {size}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <span className="text-sm text-muted-foreground">{t('memberViews.pageSize.perPage')}</span>
            </div>
          </div>
        );
      }}
    </table.Subscribe>
  );
}
